import { useState } from 'react'
import { CheckCircle, XCircle, AlertTriangle, Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { useUsuariosModulo } from '../hooks/useCEMC'
import type { CursoEstadisticas } from '../types'

interface Props {
  configId: number
  curso: CursoEstadisticas & { id: number }
}

type SortField = 'nombre_completo' | 'avance' | 'puntaje' | 'estado'
type SortDir = 'asc' | 'desc'

const ESTADO_ICON = {
  aprobado: <CheckCircle className="h-4 w-4 text-green-600" />,
  intermedio: <AlertTriangle className="h-4 w-4 text-amber-500" />,
  reprobado: <XCircle className="h-4 w-4 text-red-500" />,
}

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField | null; sortDir: SortDir }) {
  if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />
  return sortDir === 'asc'
    ? <ArrowUp className="h-3 w-3 ml-1 text-violet-600" />
    : <ArrowDown className="h-3 w-3 ml-1 text-violet-600" />
}

export function UsuariosModuloTable({ configId, curso }: Props) {
  const [moduloActivo, setModuloActivo] = useState(1)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [sortField, setSortField] = useState<SortField | null>('avance')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const { data, isLoading } = useUsuariosModulo(configId, curso.curso_id, moduloActivo, {
    page,
    page_size: 10,
    search,
    sort_field: sortField ?? '',
    sort_dir: sortDir,
  })

  const handleModuloChange = (num: number) => {
    setModuloActivo(num)
    setPage(1)
    setSortField('avance')
    setSortDir('desc')
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  const handleSort = (field: SortField) => {
    // El orden se aplica en el servidor sobre todo el dataset; volver a la
    // primera página para ver el resultado global ordenado desde el inicio.
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('asc')
    }
    setPage(1)
  }

  const rows = data?.resultados ?? []

  const thClass = 'cursor-pointer select-none hover:text-foreground transition-colors'

  return (
    <div className="space-y-3">
      {/* Selector de módulo */}
      <div className="flex items-center gap-2 w-full">
        <span className="text-xs font-medium text-muted-foreground shrink-0">Módulo:</span>
        <div className="flex flex-1 gap-2 flex-wrap">
          {Array.from({ length: curso.num_modulos }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              onClick={() => handleModuloChange(n)}
              className={`flex-1 min-w-[2.5rem] px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                moduloActivo === n
                  ? 'bg-violet-700 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-violet-100 hover:text-violet-700'
              }`}
            >
              M{n}
            </button>
          ))}
        </div>
        {data && (
          <span className="shrink-0 text-xs text-muted-foreground">
            Umbral: <strong>{data.umbral_puntaje}</strong> pts · {data.total} usuarios
          </span>
        )}
      </div>

      {/* Búsqueda */}
      <form onSubmit={handleSearch} className="flex gap-2 max-w-sm">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            className="pl-8 h-8 text-sm"
            placeholder="Buscar nombre..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <Button type="submit" size="sm" variant="outline" className="h-8">Buscar</Button>
        {search && (
          <Button type="button" size="sm" variant="ghost" className="h-8"
            onClick={() => { setSearch(''); setSearchInput(''); setPage(1) }}>
            Limpiar
          </Button>
        )}
      </form>

      {/* Tabla */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={thClass} onClick={() => handleSort('nombre_completo')}>
                <span className="inline-flex items-center">
                  Nombre <SortIcon field="nombre_completo" sortField={sortField} sortDir={sortDir} />
                </span>
              </TableHead>
              <TableHead className={`${thClass} text-center w-24`} onClick={() => handleSort('avance')}>
                <span className="inline-flex items-center justify-center w-full">
                  Avance <SortIcon field="avance" sortField={sortField} sortDir={sortDir} />
                </span>
              </TableHead>
              <TableHead className={`${thClass} text-right w-36`} onClick={() => handleSort('puntaje')}>
                <span className="inline-flex items-center justify-end w-full">
                  Puntaje <SortIcon field="puntaje" sortField={sortField} sortDir={sortDir} />
                </span>
              </TableHead>
              <TableHead className={`${thClass} text-center w-24`} onClick={() => handleSort('estado')}>
                <span className="inline-flex items-center justify-center w-full">
                  Estado <SortIcon field="estado" sortField={sortField} sortDir={sortDir} />
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {[1, 2, 3, 4].map((j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : !rows.length ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8 text-sm">
                  Sin datos para este módulo
                </TableCell>
              </TableRow>
            ) : (
              rows.map((u, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium text-sm capitalize">{u.nombre_completo ? u.nombre_completo.toLowerCase() : '—'}</TableCell>
                  <TableCell className="text-center text-sm">{u.avance}</TableCell>
                  <TableCell className="text-right text-sm">
                    {u.puntaje !== null ? u.puntaje : '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex justify-center">{ESTADO_ICON[u.estado]}</span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Paginación */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Página {data.page} de {data.total_pages}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page >= data.total_pages}
              onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
